
import {
  IDetailsRowProps,
  SelectionMode,
  IColumn,
  IDetailsCheckboxProps
} from 'office-ui-fabric-react/lib/DetailsList';
import { IRenderFunction } from '@uifabric/utilities/lib/IRenderFunction';
import * as React from 'react';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { getRowAriaLabel as defaultGetRowAriaLabel } from '../../models/items/AriaHelper';
import { getRowCheckboxAriaLabel } from '../../models/items/AriaHelper';
import { ItemDefaultDropZone, renderChildrenWithDropZoneElement } from '../defaultDropZone/DefaultDropZone';
import { mark } from '../../utilities/performance';
import { IActionStateProps, WithAction, evaluateAction } from '../withAction/WithAction';
import { itemAriaLabelControl } from '../../controls/itemAriaLabelControl';
import { IItemColumn } from '../../models/schema/IItemColumn';
import { IItemColumnRendererProps, itemColumnRenderControl } from '../../models/schema/itemColumnRenderControl';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IPropsWithDefaultRender } from '../controls/defaultRenderComponentControlOption';
import { ItemDefaultClickAction } from '../actions/defaultClick/DefaultClickAction';
import { RegisterInvokeAction } from '../decorators/withActionInvoker';
import { itemDetailsRowControl } from '../../controls/itemDetailsRowControl';
import { DeferredRender } from '../../utilities/components/DeferredRender';
import { IFaceted, pack } from '../../models/Facet';
import { createMemoizer } from '../../utilities/Memoizer';
import { KeyFacet, stringPart, numberPart } from '../../models/KeyFacet';
import { ControlFacet } from '../../models/controls/ControlFacet';
import { ComputedItemFacet, IComputedItemFacetProps } from '../../models/itemCache/ComputedItemFacet';

/**
 * @internal
 */
export const PAGE_LOAD_BARRIER_CONTEXT = React.createContext<Promise<void> | undefined>(undefined);

/**
 * Internal KeyFacet to which to bind row-binding data for a specific item
 * at a specific index within the DetailsList.
 * @internal
 */
export const itemDetailsRowKeyFacet = new KeyFacet<{
  itemKey: string;
  itemIndex: number;
}>('itemDetailsRow', {
  itemKey: stringPart(),
  itemIndex: numberPart()
});

/**
 * Internal Control to be used to pass the current columns for the DetailsList
 * to the computed facet for row-binding.
 * @internal
 */
export const itemDetailsRowColumnsControl = new ControlFacet<{}, {
  columns: IItemColumn[];
}>('itemDetailsRowColumns');

/**
 * Internal computed facet used to bind row data for an item in a DetailsList.
 * @internal
 */
export const itemDetailsRowDataFacet = new ComputedItemFacet('itemDetailsRowData', (
  itemCache: IItemCache,
  props: IComputedItemFacetProps
): IItemDetailsRowPerformanceOptimizationData | undefined => {
  const itemDetailsRow = itemCache.demandItemFacet(itemDetailsRowKeyFacet, props.itemKey);

  if (!itemDetailsRow) {
    return;
  }

  const controlHandler = itemCache.demandItemFacet(itemDetailsRowColumnsControl, undefined);

  const {
    columns
  } = itemDetailsRowColumnsControl.evaluate(controlHandler)(itemCache, {});

  if (!columns) {
    return;
  }

  const {
    itemIndex,
    itemKey
  } = itemDetailsRow;

  return getItemDetailsRowData(itemCache, {
    columns,
    itemIndex: itemIndex === -1 ? undefined : itemIndex,
    itemKey
  });
});

const columnTrimmer = createMemoizer((column: IColumn): IColumn => {
  const {
    onRender,
    ...trimmedColumn
  } = column;

  return trimmedColumn;
});

interface IItemDetailsRowBaseProps extends IPropsWithDefaultRender<IItemDetailsRowProps> {
  invokeAction: JSX.Element | null;
  renderers: IColumnRenderMap['renderers'];
  dragDropElement: JSX.Element | null;
}

class ItemDetailsRowBase extends React.PureComponent<IItemDetailsRowBaseProps> {
  public render(): JSX.Element {
    const {
      defaultRender: DetailsRow,
      invokeAction,
      renderers,
      dragDropElement,
      ...detailsRowProps
    } = this.props;

    const {
      itemKey,
      columns
    } = this.props;

    return (
      // Render the action used to open the item, so that it bubbles up its availability and execute function
      // to the action dispatcher.
      <WithAction action={ invokeAction }>
        { (invokeActionStateProps: IActionStateProps) => {
          const { isAvailable } = invokeActionStateProps;

          const isDisabled = !isAvailable && (
            !detailsRowProps.selection.canSelectItem(detailsRowProps.item) ||
            detailsRowProps.selection.mode === SelectionMode.none);

          const item: IItemColumnRendererProps & Pick<IColumnRenderMap, 'renderers'> = {
            ...detailsRowProps.item,
            isInvokable: isAvailable,
            isDisabled: isDisabled,
            renderers
          };

          const row = (
            <>
              <RegisterInvokeAction
                { ...invokeActionStateProps }
                key='invokeAction'
                itemKey={ itemKey }
                index={ detailsRowProps.itemIndex }
              />
              {
                DetailsRow ? (
                  <DetailsRow
                    { ...detailsRowProps }
                    key='row'
                    onRenderDetailsCheckbox={ onRenderDetailsCheckbox }
                    columns={ columns }
                    item={ item }
                    onRenderItemColumn={ onRenderItemColumn }
                  />
                ) : null
              }
            </>
          );

          return renderChildrenWithDropZoneElement(row, dragDropElement);
        } }
      </WithAction>
    );
  }
}

/**
 * Not intended for external use.
 * @internal
 */
export type IItemDetailsRowPerformanceOptimizationData = {
  invokeAction: JSX.Element | null;
  renderers: IColumnRenderMap['renderers'];
  dragDropElement: JSX.Element | null;
} & Pick<IDetailsRowProps, 'getRowAriaLabel' | 'checkButtonAriaLabel'>;

/**
 * Not intended for external use.
 * @internal
 */
export interface IItemDetailsRowPerformanceOptimizationProps {
  __performanceData?: IItemDetailsRowPerformanceOptimizationData;
}

/**
 * Props used to render an item as a Fabric DetailsRow.
 * @public
 */
export interface IItemDetailsRowProps extends IDetailsRowProps {
  item: IItemColumnRendererProps;

  /**
   * The key for the item to render in a row.
   */
  itemKey: string;
  /**
   * The key for the item set for the overall list.
   */
  itemSetKey: string;
}

interface IColumnRenderMap {
  renderers: {
    [key: string]: IColumn['onRender'];
  };
}

/**
 * @internal
 */
export class ItemDetailsRow extends React.PureComponent<IPropsWithDefaultRender<IItemDetailsRowProps>> {
  public render(): JSX.Element {
    const {
      props
    } = this;

    const {
      itemKey,
      itemIndex,
      columns,
      __performanceData
    } = props as IPropsWithDefaultRender<IItemDetailsRowProps> & IItemDetailsRowPerformanceOptimizationProps;

    const finalColumns = columns.map(columnTrimmer);

    return __performanceData ? (
      <ItemDetailsRowBase
        { ...props }
        { ...__performanceData }
        columns={ finalColumns }
      />
    ) : (
        <WithItemCache>
          { ({ itemCache }: IItemCacheStateProps) => {
            const performanceData = getItemDetailsRowData(itemCache, {
              itemKey,
              itemIndex,
              columns
            });

            return (
              <ItemDetailsRowBase
                { ...props }
                { ...performanceData }
                columns={ finalColumns }
              />
            );
          } }
        </WithItemCache>
      );
  }
}

function onRenderItemColumn(
  item: IItemColumnRendererProps & Pick<IColumnRenderMap, 'renderers'>,
  index?: number,
  column?: IColumn
): JSX.Element | null {
  if (!column || !column.key) {
    return null;
  }

  const onRender = item.renderers[column.key] || column.onRender;

  if (onRender) {
    return onRender(item, index, column);
  }

  return null;
}

function buildColumnRenderMap(itemCache: IItemCache, props: {
  itemKey: string;
  itemIndex?: number;
  columns: IItemColumn[];
  rowData: IFaceted;
}): IColumnRenderMap {
  const {
    itemKey,
    itemIndex,
    columns,
    rowData
  } = props;

  const renderers: IColumnRenderMap['renderers'] = {};

  for (const column of columns) {
    const {
      data
    } = column;

    const {
      renderer: onRender = column.onRender
    } = itemColumnRenderControl.evaluate(data && data.renderHandler)(itemCache, {
      itemKey,
      itemIndex,
      column,
      rowData
    });

    // TODO Pre-render the fields using the Item Cache, if the output of the renderer is compatible.

    renderers[column.key] = onRender;
  }

  return {
    renderers
  };
}

function onRenderDetailsCheckbox(
  props?: IDetailsCheckboxProps,
  defaultRender?: IRenderFunction<IDetailsCheckboxProps>
): JSX.Element {
  // tslint:disable-next-line:no-non-null-assertion
  const checkProps = props!;

  return (
    <PAGE_LOAD_BARRIER_CONTEXT.Consumer>
      { (pageLoadBarrier: Promise<void> | undefined) => {
        return (
          <DeferredRender
            barrier={ pageLoadBarrier }
          >
            { /* tslint:disable-next-line:no-non-null-assertion */ }
            { defaultRender!(checkProps) }
          </DeferredRender>
        );
      } }
    </PAGE_LOAD_BARRIER_CONTEXT.Consumer>
  );
}

/**
 * Computes the data to be bound to a DetailsRow for a given item.
 */
function getItemDetailsRowData(itemCache: IItemCache, props: {
  itemKey: string;
  itemIndex?: number;
  columns: IColumn[];
}): IItemDetailsRowPerformanceOptimizationData {
  const {
    itemKey,
    itemIndex,
    columns
  } = props;

  // TODO Rename this mark to represent that this is an execution of the selector, not a render of the row.
  mark(`ItemDetailsRow.render(${itemKey})`);

  const ariaLabelControlHandler = itemCache.demandItemFacet(itemAriaLabelControl, itemKey);

  // This is the place to bring in any props initialized from the state of the item cache.
  // Use this point to override DetailsRow props based on the state of the item.

  const {
    itemAriaLabel,
    itemCheckboxAriaLabel
  } = itemAriaLabelControl.evaluate(ariaLabelControlHandler)(itemCache, {
    itemKey: itemKey,
    itemAriaLabel: defaultGetRowAriaLabel(itemCache, { itemKey: itemKey }),
    itemCheckboxAriaLabel: getRowCheckboxAriaLabel(itemCache, { itemKey: itemKey })
  });

  const getRowAriaLabel = () => itemAriaLabel;

  const itemDetailsRowControlHandler = itemCache.demandItemFacet(itemDetailsRowControl, itemKey);

  const {
    rowData
  } = itemDetailsRowControl.evaluate(itemDetailsRowControlHandler)(itemCache, {
    itemKey: itemKey,
    rowData: pack({}, {})
  });

  const {
    renderers
  } = buildColumnRenderMap(itemCache, {
    itemKey,
    itemIndex,
    columns,
    rowData
  });

  const invokeAction = evaluateAction(itemCache, (
    <ItemDefaultClickAction itemKey={ itemKey } />
  ));

  const dragDropElement = evaluateAction(itemCache, (
    <ItemDefaultDropZone itemKey={ itemKey } enableDrag={ true } itemIndex={ itemIndex } />
  ));

  return {
    getRowAriaLabel,
    invokeAction,
    renderers,
    checkButtonAriaLabel: itemCheckboxAriaLabel,
    dragDropElement
  };
}
